import React, { useEffect, useRef, useState } from 'react';

const EASE_OUT = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Counts from the previous value to the next one on change.
 *
 * Honours prefers-reduced-motion by snapping straight to the value, and always
 * lands exactly on the target rather than on a rounded interpolation — these
 * are wages, so the final frame has to be the real number.
 */
export function useCountUp(value, { duration = 900 } = {}) {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  const frameRef = useRef(null);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduced || duration <= 0) {
      fromRef.current = target;
      setDisplay(target);
      return undefined;
    }

    const from = fromRef.current;
    if (from === target) return undefined;

    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      if (t >= 1) {
        setDisplay(target);
        fromRef.current = target;
        frameRef.current = null;
        return;
      }
      setDisplay(from + (target - from) * EASE_OUT(t));
      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      fromRef.current = target;
    };
  }, [target, duration]);

  return display;
}

/** `format` receives the interpolated number and returns the string to show. */
export default function CountUp({ value, format = (v) => String(Math.round(v)), duration, className = '' }) {
  const display = useCountUp(value, { duration });
  return <span className={className}>{format(display)}</span>;
}
